import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Target, Eye, Award, Beaker } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.6, ease: "easeOut" },
  }),
};

const cardReveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.5, ease: "easeOut" },
  }),
};

const pillars = [
  {
    icon: Target,
    title: "Our Mission",
    desc: "To redefine the impulse beverage category across retail, hospitality, and institutional sectors in India with innovative, high-quality, scalable beverage solutions.",
  },
  {
    icon: Eye,
    title: "Our Vision",
    desc: "To become India's top frozen and carbonated beverage company through a nationwide dispensing network and standout beverage brands.",
  },
  {
    icon: Award,
    title: "Our Legacy",
    desc: "Built on the proven heritage of Ultimate Post Mix Australia with 40+ years of experience in high-quality beverage dispensing solutions worldwide.",
  },
  {
    icon: Beaker,
    title: "Innovation",
    desc: "Proprietary India-focused syrup formulations combined with high-performance dispensing systems and scalable distribution technology.",
  },
];

export default function AboutSection() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start end", "end start"] });
  const bannerY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section ref={sectionRef} id="about" data-testid="about-section" className="py-16 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="max-w-3xl mb-16"
        >
          <motion.p variants={fadeUp} custom={0} className="text-[#C8D616] font-semibold text-sm uppercase tracking-widest mb-3">
            About Us
          </motion.p>
          <motion.h2 variants={fadeUp} custom={1} className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0B1D3A] leading-tight mb-6">
            Pioneering Frozen & Carbonated Beverage Innovation in India
          </motion.h2>
          <motion.p variants={fadeUp} custom={2} className="text-base lg:text-lg text-slate-600 leading-relaxed">
            Ultimate Infusions Private Limited is a forward-thinking beverage innovation company
            focused on introducing next-generation Frozen Carbonated Beverage (FCB) and post-mix
            systems to the Indian market. We specialise in deploying and servicing high-performance
            beverage dispensing systems, combined with proprietary, India-focused syrup formulations
            to create a high-margin, scalable beverage ecosystem.
          </motion.p>
        </motion.div>

        {/* Pillars - staggered reveal */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {pillars.map((item, i) => (
            <motion.div
              key={item.title}
              variants={cardReveal}
              custom={i}
              className="group p-6 lg:p-8 rounded-2xl border border-slate-100 bg-slate-50/50 hover:bg-white hover:shadow-xl hover:shadow-blue-500/5 hover:border-blue-100 transition-all duration-500"
            >
              <div className="w-12 h-12 rounded-xl bg-[#0B1D3A] flex items-center justify-center mb-5 group-hover:bg-[#C8D616] transition-colors duration-500">
                <item.icon size={22} className="text-white group-hover:text-[#0B1D3A] transition-colors duration-500" />
              </div>
              <h3 className="text-lg font-bold text-[#0B1D3A] mb-3">{item.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Heritage banner - subtle parallax */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="mt-16 p-8 lg:p-12 rounded-2xl bg-gradient-to-r from-[#0B1D3A] to-[#1A365D] text-white relative overflow-hidden"
        >
          <motion.div className="absolute inset-0 opacity-5"
            style={{
              backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
              backgroundSize: "30px 30px",
              y: bannerY,
            }}
          />
          <div className="relative grid lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-2">
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                Backed by 40+ Years of Global Excellence
              </h3>
              <p className="text-slate-300 leading-relaxed">
                Our foundation draws from the proven legacy of Ultimate Post Mix, Australia &mdash;
                a trusted name with over four decades in high-quality beverage dispensing solutions,
                exporting to markets worldwide. This heritage strengthens our focus on dependable
                technology, premium formulations, and strong service support.
              </p>
            </div>
            <div className="flex items-center justify-center lg:justify-end">
              <div className="text-center">
                <div className="text-6xl lg:text-7xl font-extrabold text-[#C8D616]">40+</div>
                <div className="text-sm text-slate-400 mt-2 uppercase tracking-wider">Years of Excellence</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
